import { ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import TextInput from "@galaxy-io/dls/inputs/TextInput";
import Text, { TextSize, TextVariant } from "@galaxy-io/dls/text/Text";
import { type ImportSource, manualSourceSchema } from "@gtm/contracts/imports";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { DrawerStack } from "@/components/workspace/drawer-content";
import { SelectField } from "@/components/workspace/select-field";
import { Feedback } from "@/components/workspace/status";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { useDialogReset } from "@/components/workspace/use-dialog-reset";
import { apiClient, unwrap } from "@/lib/api-client";

const platforms = [
  { value: "slack", label: "Slack" },
  { value: "discord", label: "Discord" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "event", label: "Event" },
  { value: "forum", label: "Community forum" },
  { value: "other", label: "Other" },
];
const urlHints: Record<string, string> = {
  slack: "https://yourcommunity.slack.com/archives/C0123456",
  discord: "https://discord.com/channels/123456789",
  linkedin: "https://www.linkedin.com/company/acme",
  event: "https://lu.ma/your-event",
};

export function NewSourceDialog({
  open = true,
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (source: ImportSource) => Promise<void>;
  open?: boolean;
}) {
  const [name, setName] = useState("");
  const [provider, setProvider] = useState("slack");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const parsed = manualSourceSchema.safeParse({ name, provider, url });
  const urlError = url && !manualSourceSchema.shape.url.safeParse(url).success;
  useDialogReset(open, () => {
    setName("");
    setProvider("slack");
    setUrl("");
    setBusy(false);
    setError("");
  });
  async function save() {
    if (!parsed.success) return;
    setBusy(true);
    setError("");
    try {
      await onSaved(await unwrap(apiClient().POST("/imports/sources", { body: parsed.data })));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create the source.");
      setBusy(false);
    }
  }
  return (
    <SurfaceDialog
      open={open}
      flush
      title="New source"
      headerDescription="Where the rows in your file came from"
      busy={busy}
      dirty={!!(name || url)}
      onClose={onClose}
      footer={
        <>
          <Button
            label="Cancel"
            variant={ButtonVariant.SECONDARY}
            isDisabled={busy}
            onClick={onClose}
          />
          <Button
            label="Create source"
            isLoading={busy}
            isDisabled={busy || !parsed.success}
            onClick={() => void save()}
          />
        </>
      }
    >
      <DrawerStack>
        <TextInput
          label="Name"
          placeholder="e.g. Acme community #introductions"
          value={name}
          onChange={setName}
          fillWidth
        />
        <SelectField label="Platform" value={provider} options={platforms} onChange={setProvider} />
        <TextInput
          label="URL"
          placeholder={urlHints[provider] ?? "https://…"}
          value={url}
          onChange={setUrl}
          error={urlError ? "Use a full http:// or https:// link." : undefined}
          fillWidth
        />
        <Text size={TextSize.BODY_SM} variant={TextVariant.TERTIARY}>
          {provider === "slack"
            ? "Use the channel link. Message URLs in your file must belong to this workspace and channel."
            : "Imported records link back to this source as their provenance."}
        </Text>
        <Feedback message={error} error />
      </DrawerStack>
    </SurfaceDialog>
  );
}
